import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Tag, Table, Button, Space } from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  AppointmentStatusType,
  APPOINTMENT_STATUS_VALUES,
  APPOINTMENT_STATUSES
} from 'constants/appointments';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { REPORT_STATUS } from 'constants/reports';
import { PLAN_TYPE_VALUES } from 'constants/profile';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import { ColumnProps } from 'antd/lib/table';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import { PageWrapper } from 'components/ui';
// import { money } from 'utils/price';
import { getStatusColor } from 'utils/appointments';
import { getIdStart } from 'utils/string';
import { FormFilters } from './components/Filters/types';
import { Filters } from './components';
import { showRestError } from 'utils/showErrorMessage';
import { HowItWorksLink, ViewReport } from 'components/shared';
import { getFileUrl } from 'utils/file';

const PAGE_LIMIT = 10;

const Appointments: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const initialValues = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return {
      name: params.get('name') || undefined,
      type: params.get('type') || undefined,
      status: params.get('status') || undefined
    };
  }, []);

  const page = Number(searchParams.get('page') || 1);

  // states
  const [filters, setFilters] = useState<FormFilters>(initialValues);
  const [appointmentsData, setAppointmentsData] = useState<LegacyAppointment[]>(
    []
  );

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchAppointments({
      name: filters.name,
      plan_type: filters.type,
      status: filters.status ? Number(filters.status) : undefined,
      page,
      limit: PAGE_LIMIT
    })
      .then(res => {
        setAppointmentsData(res.results);
        setTotal(res.total);
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [filters.name, filters.type, filters.status, page]);

  const renderReportButton = useCallback(
    (record: LegacyAppointment) => {
      const isVideoCall = record?.plan?.type === PLAN_TYPE_VALUES.video_call;

      if (
        record.status === APPOINTMENT_STATUSES.finished ||
        (!isVideoCall && record.paid)
      ) {
        if (record?.report?.status === REPORT_STATUS.sent) {
          return <ViewReport id={record.report.id} />;
        }

        if (record?.reports_file?.file) {
          return (
            <a
              href={getFileUrl(record.reports_file.file)}
              target="_blank"
              download={record.reports_file.file}
              rel="noreferrer"
            >
              <Button
                style={{ minWidth: 135 }}
                icon={<DownloadOutlined />}
                type="primary"
                ghost
              >
                {t('reports.download')}
              </Button>
            </a>
          );
        }

        if (record?.report?.status === REPORT_STATUS.draft) {
          return (
            <Link to={`/report/details/${record.report.id}?id=${record.id}`}>
              <Button style={{ minWidth: 135 }} icon={<EditOutlined />}>
                {t('reports.edit_report')}
              </Button>
            </Link>
          );
        }

        return (
          <Link to={`/report/details?id=${record.id}`}>
            <Button style={{ minWidth: 135 }} icon={<PlusOutlined />}>
              {t('reports.add_report')}
            </Button>
          </Link>
        );
      }

      return null;
    },
    [t]
  );

  const columns: ColumnProps<LegacyAppointment>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => getIdStart(id)
      },
      {
        title: t('appointments.patient_name'),
        dataIndex: 'patient',
        key: 'patient',
        render: value => `${value?.first_name} ${value?.last_name}`
      },
      {
        title: t('appointments.date_time'),
        dataIndex: 'dates',
        key: 'dates',
        render: (value: { date: string; accepted: boolean }[]) => {
          const dateItem = value.find(item => item.accepted);

          if (dateItem) {
            return dayjs(dateItem.date).format(FULL_DATE_FORMAT);
          }

          return '-';
        }
      },
      {
        title: <span>{t('appointments.status')}</span>,
        dataIndex: 'status',
        key: 'status',
        render: (value: AppointmentStatusType) => (
          <Tag color={getStatusColor(value)}>
            {t(`appointments.${APPOINTMENT_STATUS_VALUES[value]}`)}
          </Tag>
        )
      },
      {
        title: <span>{t('appointments.payment')}</span>,
        dataIndex: 'admin_paid',
        key: 'admin_paid',
        render: (value: boolean) =>
          value ? t(`appointments.paid`) : t(`appointments.not_paid`)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (id, record) => (
          <Space>
            {renderReportButton(record)}
            <div>
              <Link to={`/appointments/details/${id}`}>
                <Button icon={<EyeOutlined />}>
                  {t('appointments.view_appointment')}
                </Button>
              </Link>
            </div>
          </Space>
        )
      }
    ],
    [renderReportButton, t]
  );

  const appointments = appointmentsData;

  useEffect(() => {
    dispatch(setTour({ key: 'appointment', value: !!appointments.length }));
  }, [appointments.length, dispatch]);

  return (
    <PageWrapper
      title={t(`appointments.page_title`)}
      color="white"
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.APPOINTMENTS.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <Filters
        onSubmit={values => {
          searchParams.set('page', '1');
          Object.entries(values).forEach(([key, value]) => {
            searchParams.set(key, value && value !== 'undefined' ? value : '');
          });
          setSearchParams(searchParams);
          setFilters({
            name: values.name && values.name !== 'undefined' ? values.name : '',
            type: values.type && values.type !== 'undefined' ? values.type : '',
            status:
              values.status && values.status !== 'undefined'
                ? values.status
                : ''
          });
        }}
        initialValues={initialValues}
      />
      <Table
        dataSource={appointments}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total || 0,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          }
        }}
        scroll={{ x: 768 }}
      />
    </PageWrapper>
  );
};

export default Appointments;
